import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { getRequiredCurrentUserClientId } from "../../../core/auth/tenant";
import {
  getClientNotificationSettings,
  toNotificationSettings,
  updateClientNotificationSettings,
} from "../services/notificationsService";

const getInitialSettings = () => toNotificationSettings(null);

export const fetchNotificationSettingsThunk = createAsyncThunk(
  "notifications/fetchNotificationSettings",
  async (clientId, { getState, rejectWithValue }) => {
    const resolvedClientId = Number(clientId) || getRequiredCurrentUserClientId(getState());
    const response = await getClientNotificationSettings(resolvedClientId);
    if (response?.error) {
      return rejectWithValue(
        response.error.message || "Unable to load notification settings.",
      );
    }

    const client = response.client;
    return { client, settings: toNotificationSettings(client) };
  },
);

export const saveNotificationSettingsThunk = createAsyncThunk(
  "notifications/saveNotificationSettings",
  async (_, { getState, rejectWithValue }) => {
    const state = getState().notifications;
    const client = state.client;
    if (!client) {
      return rejectWithValue("Client data is unavailable.");
    }

    const response = await updateClientNotificationSettings(client, state.settings);
    if (response?.error) {
      return rejectWithValue(
        response.error.message || "Unable to save notification settings.",
      );
    }

    return {
      client: response.client || { ...client, ...state.settings },
      settings: { ...state.settings },
    };
  },
);

const initialState = {
  client: null,
  settings: getInitialSettings(),
  savedSettings: getInitialSettings(),
  isLoading: true,
  isSaving: false,
  errorMessage: "",
};

const notificationsSlice = createSlice({
  name: "notifications",
  initialState,
  reducers: {
    toggleNotificationSetting: (state, action) => {
      const key = action.payload;
      if (!Object.prototype.hasOwnProperty.call(state.settings, key)) {
        return;
      }
      state.settings[key] = !state.settings[key];
    },
    clearNotificationError: (state) => {
      state.errorMessage = "";
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchNotificationSettingsThunk.pending, (state) => {
        state.isLoading = true;
        state.errorMessage = "";
      })
      .addCase(fetchNotificationSettingsThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.client = action.payload.client;
        state.settings = action.payload.settings;
        state.savedSettings = action.payload.settings;
      })
      .addCase(fetchNotificationSettingsThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.errorMessage = action.payload || "Unable to load notification settings.";
      })
      .addCase(saveNotificationSettingsThunk.pending, (state) => {
        state.isSaving = true;
        state.errorMessage = "";
      })
      .addCase(saveNotificationSettingsThunk.fulfilled, (state, action) => {
        state.isSaving = false;
        state.client = action.payload.client;
        state.savedSettings = action.payload.settings;
      })
      .addCase(saveNotificationSettingsThunk.rejected, (state, action) => {
        state.isSaving = false;
        state.errorMessage = action.payload || "Unable to save notification settings.";
      });
  },
});

export const { clearNotificationError, toggleNotificationSetting } = notificationsSlice.actions;

export const selectNotificationsState = (state) => state.notifications;

export default notificationsSlice.reducer;
